import { message } from "antd";
import { useCallback, useContext, useMemo, useState } from "react";
import { useLoadScenario } from "../../externalComponents/contexts/LoadScenarioContext";
import ButtonToggleGroup from "../../externalComponents/flow_editor/shared/panel/components/ButtonToggleGroup";
import { handleMessageMultipleChanges } from "../../externalComponents/handleMessageMultipleChanges";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import { saveScenarioFlow } from "../../functions/saveScenarioFlow";
import { CustomerContext } from "../../providers";

/**
 * @deprecated SP-9214 TOREMOVE
 */
const DialogConvertAnswer = ({
  nodes,
  setNodes,
  data,
  setData,
  edges,
  scenario,
  campaignId,
  campaignName,
  isLoading,
  setIsLoading,
  setIsEditingInput,
  setModifications,
}) => {
  const { refreshFlow } = useLoadScenario();
  const { user } = useContext(CustomerContext);
  const selectedNode = useMemo(() => {
    return nodes.find((n) => n.selected);
  }, [nodes]);

  const [buttonConflictChoice, setButtonConflictChoice] = useState(
    selectedNode?.data?.urlButton && selectedNode?.data?.buttons?.length > 0
      ? "urlButton"
      : selectedNode?.data?.buttons?.length > 0
        ? "buttons"
        : null,
  );

  const closeModal = useCallback(() => {
    setData((_listData) => ({
      ..._listData,
      isModalOpen: false,
    }));
  }, []);

  const handleConfirmation = async () => {
    try {
      // Pass the user's choice in the node's data for backend processing
      const changes = { shouldBeConverted: true };
      if (
        selectedNode?.data?.urlButton &&
        selectedNode?.data?.buttons?.length > 0 &&
        buttonConflictChoice
      ) {
        changes.conflictResolutionChoice = buttonConflictChoice;
      }
      handleMessageMultipleChanges(changes, nodes, setNodes, selectedNode);
      setIsLoading(true);
      await saveScenarioFlow(
        nodes,
        edges,
        scenario.id,
        campaignId,
        user,
        campaignName,
      );
      setIsEditingInput(false);
      setIsLoading(false);
      setModifications(false);
      message.success(`Flow successfully saved`);

      refreshFlow();
    } finally {
      closeModal();
    }
  };

  return (
    <Dialog
      key="dialog_convert_to_message"
      open={data.isModalOpen}
      onClose={() => closeModal()}
    >
      <DialogTitle>Convert the template into a message</DialogTitle>
      <DialogBody className="flex-1 overflow-y-auto scrollbar">
        <div className="space-y-4">
          {isLoading ? (
            <p className="text-gray-700">
              Converting template to message... This may take a few seconds.
            </p>
          ) : (
            <>
              <p className="text-gray-700">
                The conversion of this template to a message will be performed
                with the following changes:
              </p>

              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                {selectedNode?.data?.headerParameters?.image && (
                  <li>✅ The media (image) will be kept in the message</li>
                )}
                {selectedNode?.data?.headerParameters?.video && (
                  <li>✅ The media (video) will be kept in the message</li>
                )}
                {selectedNode?.data?.headerParameters?.document && (
                  <li>✅ The media (document) will be kept in the message</li>
                )}
                <li className="text-red-600">
                  ❌ All connections will be removed
                </li>
                {selectedNode?.data?.urlButton?.text?.length > 20 && (
                  <li className="text-red-600">
                    ❌ URL button text exceeds the maximum number (20) of
                    characters and will be truncated:
                    <div className="pl-8 mt-1">
                      <div>From: "{selectedNode.data.urlButton.text}"</div>
                      <div>
                        To: "{selectedNode.data.urlButton.text.substring(0, 20)}
                        "
                      </div>
                    </div>
                  </li>
                )}
                {selectedNode?.data?.buttons?.length > 3 && (
                  <li className="text-red-600">
                    ❌ Only the first 3 buttons will be kept
                  </li>
                )}
                {selectedNode?.data?.urlButton2 && (
                  <li className="text-red-600">
                    ❌ The second URL button will be removed
                  </li>
                )}
                {selectedNode?.data?.footer &&
                  !selectedNode?.data?.buttons?.length && (
                    <li className="text-red-600">
                      ❌ The footer will be removed because it requires at least
                      one button
                    </li>
                  )}
              </ul>

              {selectedNode?.data?.urlButton &&
                selectedNode?.data?.buttons?.length > 0 && (
                  <ButtonToggleGroup
                    label="Choose which to keep:"
                    options={[
                      {
                        value: "urlButton",
                        label: "Keep URL button (remove reply buttons)",
                      },
                      {
                        value: "buttons",
                        label: "Keep reply buttons (remove URL button)",
                      },
                    ]}
                    selectedValue={buttonConflictChoice}
                    onSelect={setButtonConflictChoice}
                    className="mt-4 mx-1"
                  />
                )}

              <p className="text-gray-700 mt-4">
                The conversion will require manual adjustments.
              </p>
            </>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={() => closeModal()}
          className="min-w-[100px] h-10"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          color="primaryButton"
          onClick={() => handleConfirmation()}
          className="min-w-[100px] h-10"
          disabled={
            isLoading ||
            (selectedNode?.data?.urlButton &&
              selectedNode?.data?.buttons?.length > 0 &&
              !buttonConflictChoice)
          }
        >
          {isLoading ? "Processing..." : "Convert in message"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogConvertAnswer;
